/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import groupBy from "lodash/groupBy";
import shaka from "shaka-player";

import { ClientSideAdvertisementShaka, loadIMALib } from "./Advertisement";
import {
  CastSender,
  ChromecastPlayerState,
  PlaybackState,
} from "./ChromecastSender";
import { FairPlayLoader } from "./DRM";
import { getErrorMessageByCategoryCode, ShakaErrorEvent } from "./error";
import { PlayerEvent } from "./events";
import {
  isFairPlaySource,
  isPlayReadySource,
  isWidevineSource,
} from "./guards";
import { getPlayerRuntimeConfig } from "./playerConfig";
import {
  AirPlayAvailability,
  AirPlayAvailabilityChangeEvent,
  AirPlayEvent,
  ControllerOptions,
  PlayerAudioTrack,
  PlayerController,
  PlayerSource,
  PlayerSubtitleTrack,
  PlayerVideoTrack,
} from "./types";

shaka.polyfill.installAll();

type CurrentMediaState = {
  source: PlayerSource;
  autoplay?: boolean;
  startPosition?: number;
};

type TLanguageRoleUsable = shaka.extern.LanguageRole & { label: string };

const isAndroid = () => /Android/i.test(navigator.userAgent);

function getLiveTuningConfig() {
  const config = getPlayerRuntimeConfig();
  const tuning = config.liveTuning;
  const shaka = tuning?.shaka;

  return {
    liveTuningEnabled: tuning?.enabled ?? false,
    liveDefaultBandwidth: tuning?.defaultBandwidth ?? 1800000,
    liveMaxHeight: tuning?.maxHeight ?? 0,
    shakaBufferingGoal: shaka?.bufferingGoal ?? 20,
    shakaRebufferingGoal: shaka?.rebufferingGoal ?? 6,
    shakaBufferBehind: shaka?.bufferBehind ?? 30,
    shakaStallThreshold: shaka?.stallThreshold ?? 1.5,
    shakaSafeSeekOffset: shaka?.safeSeekOffset ?? 5,
    shakaAbrSwitchInterval: shaka?.switchInterval ?? 8,
    shakaLowLatencyMode: shaka?.lowLatencyMode ?? false,
    liveEnableStallRecovery: true,
    liveStallRecoveryMs: 3000,
  };
}

export class ShakaPlayerController implements PlayerController {
  private readonly options: ControllerOptions;
  castSender?: CastSender;

  private player: shaka.Player | null = null;
  private eventManager: shaka.util.EventManager;
  private currentMediaState: CurrentMediaState | null = null;
  private clientSideAdvertisement: ClientSideAdvertisementShaka | undefined;
  private fairPlayLoader: FairPlayLoader | undefined;
  private adsInit = false;
  private isOnFullScreen = false;
  private lastError: {
    message: string;
    messageKey?: string;
    code: number;
    category: number;
  } | null = null;
  private liveRecoveryTimeoutId: number | null = null;

  constructor(options: ControllerOptions) {
    this.options = options;
    this.eventManager = new shaka.util.EventManager();
    this.fairPlayLoader = new FairPlayLoader();
  }

  initialize(): void {
    const { videoElement, imaContainer, onReady, withClientSideAds } =
      this.options;

    const player = new shaka.Player();
    player.setVideoContainer(this.options.videoElement.parentElement);
    videoElement.setAttribute("playsinline", "");

    player.attach(videoElement as HTMLMediaElement).then(() => {
      this.setupCastSender(videoElement);

      const lt = getLiveTuningConfig();
      const playerConfig = {
        drm: {
          retryParameters: { maxAttempts: 3 },
        },
        manifest: {
          dash: {
            ignoreMinBufferTime: true,
          },
        },
      } as Record<string, unknown>;
      if (lt.liveTuningEnabled) {
        playerConfig.streaming = {
          lowLatencyMode: lt.shakaLowLatencyMode,
          bufferingGoal: lt.shakaBufferingGoal,
          rebufferingGoal: lt.shakaRebufferingGoal,
          bufferBehind: lt.shakaBufferBehind,
          stallEnabled: true,
          stallThreshold: lt.shakaStallThreshold,
          safeSeekOffset: lt.shakaSafeSeekOffset,
        };
        playerConfig.abr = {
          defaultBandwidthEstimate: lt.liveDefaultBandwidth,
          switchInterval: lt.shakaAbrSwitchInterval,
          ...(lt.liveMaxHeight > 0
            ? {
                restrictions: {
                  maxHeight: lt.liveMaxHeight,
                },
              }
            : {}),
        };
      }
      player.configure(playerConfig);

      // To play M3U8 stream on an Apple device / Safari with AirPlay support, enable native HLS
      if (this.options.isAppleDevice) {
        player.configure({
          streaming: {
            useNativeHlsOnSafari: true,
          },
        });
      }

      if (withClientSideAds && !this.adsInit) {
        this.adsInit = true;

        loadIMALib()
          .then((ima) => {
            this.clientSideAdvertisement = new ClientSideAdvertisementShaka(
              ima,
              player,
              {
                imaContainerElement: imaContainer,
                videoElement,
                playerEventsManager: this.eventManager,
              },
            );
          })
          .catch((e) => {
            console.error("Unable to load Google IMA SDK", { e });
          })
          .finally(() => {
            onReady();
          });
      } else {
        onReady();
      }

      this.eventManager.listen(videoElement, "error", (event) => {
        const customEvent = event as ShakaErrorEvent;
        this.lastError = customEvent.detail;
        videoElement.dispatchEvent(new CustomEvent("custom-error"));
      });

      this.eventManager.listen(player, "buffering", (event) => {
        if (!lt.liveTuningEnabled || !lt.liveEnableStallRecovery) {
          return;
        }

        const buffering =
          (event as { buffering?: boolean; detail?: { buffering?: boolean } })
            ?.buffering ??
          (event as { detail?: { buffering?: boolean } }).detail?.buffering ??
          false;

        if (!buffering) {
          this.clearLiveRecoveryTimer();
          return;
        }

        if (!this.player?.isLive()) {
          return;
        }

        this.clearLiveRecoveryTimer();
        this.liveRecoveryTimeoutId = window.setTimeout(() => {
          this.seekToLiveEdge();
          this.clearLiveRecoveryTimer();
        }, lt.liveStallRecoveryMs);
      });

      const source = this.currentMediaState?.source;

      if (source && source.headers) {
        player
          ?.getNetworkingEngine()
          ?.registerRequestFilter((type, request) => {
            if (
              type === shaka.net.NetworkingEngine.RequestType.MANIFEST ||
              type === shaka.net.NetworkingEngine.RequestType.SEGMENT ||
              type === shaka.net.NetworkingEngine.RequestType.LICENSE
            ) {
              for (const key in source.headers) {
                request.headers[key] = source.headers[key];
              }
            }
          });
      }

      this.player = player;
    });
  }

  async loadMedia(
    source: PlayerSource,
    autoplay?: boolean,
    startPosition?: number,
  ) {
    const sourceId = source.id;

    if (sourceId && this.castSender?.isConnected()) {
      this.castSender?.load({
        source: `${sourceId}`,
        sessionToken: this.options.sessionToken,
        startTime: startPosition,
      });
    }

    this.currentMediaState = {
      source,
      autoplay,
      startPosition,
    };

    const videoEl = this.player?.getMediaElement();

    if (videoEl) {
      await this.configureDRM(source);
      this.fairPlayLoader?.injectLoader(videoEl);
    }

    this.player
      ?.load(source.src, startPosition)
      .then(() => {
        if (source.adsTagUrl) {
          this.clientSideAdvertisement?.initialize(
            source.adsTagUrl,
            // Apple does not allow to start ads with sound, it will block ads
            // playback and in result ad will be skipped by SDK, we need to mute
            // ad before play to correctly show ads on iPad
            this.options.isAppleDevice,
          );
        } else {
          videoEl?.dispatchEvent(new CustomEvent("nopreroll"));
          videoEl?.dispatchEvent(new CustomEvent("nopostroll"));
        }

        if (autoplay) {
          this.play();
        }
      })
      .then(() => {
        if (source.subtitles) {
          Promise.all(
            source.subtitles.map(
              (subtitle) =>
                this.player?.addTextTrackAsync(
                  subtitle.ContentUrl,
                  subtitle.LanguageCode,
                  "subtitles",
                  subtitle.ContentType, // mimeType
                  undefined, // codec
                  subtitle.Label, // label
                  // forced
                ),
            ),
          ).catch((_err) => {
            // Silent error as it is not crucial.
          });
        }
      })
      .catch((e) => {
        videoEl?.dispatchEvent(
          new CustomEvent("error", {
            detail: {
              category: e.category,
              code: e.code,
              message: e.message,
            },
          }),
        );
      });
  }

  dispose(): void {
    this.clearLiveRecoveryTimer();
    this.clientSideAdvertisement?.dispose();
    this.eventManager.removeAll();
    this.player?.destroy();
    this.castSender?.destroy();
  }

  pause(): void {
    if (this.castSender?.isConnected()) {
      this.castSender?.pause();
      return;
    }

    this.player?.getMediaElement()?.pause();
  }

  play(): void {
    if (this.castSender?.isConnected()) {
      this.castSender?.play();
      return;
    }

    this.player?.getMediaElement()?.play();
  }

  resume(startPosition: number): void {
    const videoEl = this.player?.getMediaElement();
    if (!videoEl) {
      return;
    }

    videoEl.currentTime = startPosition;
    this.play();

    const sourceId = this.currentMediaState?.source.id;
    if (sourceId && this.castSender?.isConnected()) {
      this.castSender?.load({
        source: `${sourceId}`,
        sessionToken: this.options.sessionToken,
        startTime: startPosition,
      });
    }
  }

  isPaused(): boolean {
    if (this.castSender?.isConnected()) {
      return !this.castSender?.isPlaying();
    }

    return Boolean(this.player?.getMediaElement()?.paused);
  }

  isLive(): boolean {
    return Boolean(this.player?.isLive());
  }

  liveSeekableStart(): number {
    return this.player?.seekRange().start || 0;
  }

  liveSeekableEnd(): number {
    return this.player?.seekRange().end || 0;
  }

  liveWindow(): number {
    const seekRange = this.player?.seekRange();
    if (!seekRange) {
      return 0;
    }
    return seekRange.end - seekRange.start;
  }

  atLiveEdge(): boolean {
    const videoEl = this.player?.getMediaElement();
    if (!videoEl || !this.player) {
      return false;
    }
    const currentTime = videoEl.currentTime;
    const seekRangeEnd = this.player.seekRange().end;
    const liveEdgeThreshold = 2; // seconds, adjust as needed
    return seekRangeEnd - currentTime < liveEdgeThreshold;
  }

  seekToLiveEdge(): void {
    if (!this.isLive()) {
      return;
    }
    const videoEl = this.player?.getMediaElement();

    if (!videoEl) {
      return;
    }

    videoEl.currentTime = this.liveSeekableEnd();
  }

  liveCurrentTime(): number {
    const videoEl = this.player?.getMediaElement();
    if (!this.isLive() || !videoEl) {
      return 0;
    }
    return videoEl.currentTime || 0;
  }

  currentTime(position?: number): number {
    const videoEl = this.player?.getMediaElement();

    if (!videoEl) {
      return 0;
    }

    if (position !== undefined) {
      videoEl.currentTime = position;
      this.castSender?.seekTo(position);
    }

    if (this.castSender?.isConnected()) {
      return this.castSender?.getCurrentTime() || videoEl.currentTime || 0;
    }

    return videoEl.currentTime || 0;
  }

  seekForward(seconds: number) {
    const currentTime = this.currentTime();
    const duration = this.isLive() ? this.liveSeekableEnd() : this.duration();

    this.currentTime(
      currentTime + seconds <= duration ? currentTime + seconds : duration,
    );
  }

  seekBackward(seconds: number) {
    const currentTime = this.currentTime();
    const seekableStart = this.liveSeekableStart();

    let newTime: number;

    switch (true) {
      case seekableStart && currentTime - seconds <= seekableStart:
        newTime = seekableStart;
        break;
      case currentTime >= seconds:
        newTime = currentTime - seconds;
        break;
      default:
        newTime = 0;
    }

    this.currentTime(newTime);
  }

  duration(): number {
    return this.player?.getMediaElement()?.duration || 0;
  }

  muted(state?: boolean): boolean {
    const videoEl = this.player?.getMediaElement();

    if (!videoEl) {
      return false;
    }

    if (state !== undefined) {
      if (this.isCastConnected()) {
        this.castSender?.setMuted(state);
      } else {
        videoEl.muted = state;
        this.clientSideAdvertisement?.muted(state);
      }
    }

    if (this.isCastConnected()) {
      return this.castSender?.isMuted() || false;
    }

    return Boolean(videoEl.muted);
  }

  volume(value?: number): number {
    const videoEl = this.player?.getMediaElement();

    if (!videoEl) {
      return 0;
    }

    if (value !== undefined) {
      if (this.isCastConnected()) {
        this.castSender?.setVolume(value);
      } else {
        videoEl.volume = value;
        this.clientSideAdvertisement?.volume(value);
      }
    }

    if (this.isCastConnected()) {
      return this.castSender?.getVolume() || 0;
    }

    return videoEl.volume || 0;
  }

  playbackRate(value?: number) {
    const videoEl = this.player?.getMediaElement();

    if (!videoEl) {
      return 1;
    }

    if (value !== undefined) {
      videoEl.playbackRate = value;
    }

    return (videoEl.playbackRate || 1) as number;
  }

  subtitleTracks(): PlayerSubtitleTrack[] {
    const tracks: shaka.extern.TrackList = this.player?.getTextTracks() || [];

    return Array.from(tracks)
      .filter(
        (track) =>
          !!track.kind && ["subtitles", "subtitle"].includes(track.kind),
      )
      .map((track) => {
        return {
          id: track.id.toString(),
          label: track.label ?? "",
          active: track.active,
          language: track.language,
        };
      });
  }

  audioTracks(): PlayerAudioTrack[] {
    if (!this.player) {
      return [];
    }
    const audioTracks = this.player
      .getAudioLanguagesAndRoles()
      .filter((audioTrack): audioTrack is TLanguageRoleUsable =>
        Boolean(audioTrack.label),
      )
      .map<PlayerAudioTrack>((audio) => ({
        id: this._createAudioTrackId(audio.label, audio.language),
        label: audio.label,
        active: false,
        language: audio.language,
      }));

    const variantTracks = this.player.getVariantTracks();
    for (const variant of variantTracks) {
      if (variant.active) {
        const activeAudioTrack = audioTracks.find(
          (audioTrack) => audioTrack.label === variant.label,
        );
        if (activeAudioTrack) {
          activeAudioTrack.active = true;
          break;
        }
      }
    }

    return audioTracks;
  }

  videoTracks(): PlayerVideoTrack[] {
    const tracks: shaka.extern.TrackList =
      this.player
        ?.getVariantTracks()
        .filter((track) => typeof track.videoId === "number") || [];

    if (!tracks || tracks.length === 0) {
      return [];
    }

    const tracksSource = Array.from(tracks);

    const activeTrack = tracks.find((track) => track.active);
    const activeTrackAudioLabel = activeTrack?.label;
    // TODO If selection is not accurate then track selection
    // could also base on roles, and maybe even fallback to
    // language property.
    const isAbleToMatchTrackAudio =
      activeTrackAudioLabel &&
      tracksSource.some((track) => track.label === activeTrackAudioLabel);

    const availableTracks = tracksSource
      .filter((track) => {
        const { width = 0, height = 0 } = track;
        const pixels = Math.min(width!, height!);

        return pixels > 0;
      })
      .filter((track) =>
        isAbleToMatchTrackAudio ? track.label === activeTrackAudioLabel : true,
      )
      .map((track) => {
        const {
          id,
          width = 0,
          height = 0,
          bandwidth: bitrate,
          frameRate,
        } = track;

        const pixels = Math.min(width!, height!);
        const label = `${pixels}p`;

        return {
          id: id.toString(),
          width,
          height,
          bitrate,
          label,
          active: track.active,
          framerate: frameRate,
        };
      })
      .sort((a, b) => (a.height! > b.height! ? -1 : 1));

    const tracksByLabel = groupBy(availableTracks, (item) => item.label);
    const tracksWithLabels = Object.entries(tracksByLabel).flatMap(
      ([label, tracks]) => {
        if (tracks.length < 2) {
          return tracks;
        } else {
          return isAbleToMatchTrackAudio
            ? tracks.map((track: any, index: any) => ({
                ...track,
                label: `${label}(${index + 1})`,
              }))
            : // If we are not matching tracks audio (isAbleToMatchTrackAudio = false),
              // then multiple tracks for the same resolution are probably the same track
              // with different audio which will pollute UI with many options,
              // as something is clearly wrong, only one track per resolution will
              // be shown to the user.
              [tracks[0]];
        }
      },
    );

    return tracksWithLabels as PlayerVideoTrack[];
  }

  onAudioTrackChange(callback: () => void): () => void {
    this.eventManager.listen(this.player, "adaptation", (event) => {
      type TrackEvent = Event & {
        oldTrack?: shaka.extern.Track;
        newTrack?: shaka.extern.Track;
      };
      const { oldTrack, newTrack } = event as TrackEvent;
      if (!oldTrack || !newTrack) {
        return;
      }
      if (oldTrack.label === newTrack.label) {
        return;
      }
      callback();
    });
    this.eventManager.listen(this.player, "variantchanged", callback);

    return () => {
      this.eventManager.unlisten(this.player, "variantchanged");
      this.eventManager.unlisten(this.player, "adaptation");
    };
  }

  onSubtitleTrackChange(callback: () => void): () => void {
    this.eventManager.listen(this.player, "texttrackvisibility", callback);
    this.eventManager.listen(this.player, "textchanged", callback);
    this.eventManager.listen(this.player, "trackschanged", callback);

    return () => {
      this.eventManager.unlisten(this.player, "texttrackvisibility");
      this.eventManager.unlisten(this.player, "textchanged");
      this.eventManager.unlisten(this.player, "trackschanged");
    };
  }

  onVideoTrackChange(callback: () => void): () => void {
    this.eventManager.listen(this.player, "variantchanged", callback);
    this.eventManager.listen(this.player, "adaptation", callback);

    return () => {
      this.eventManager.unlisten(this.player, "variantchanged");
      this.eventManager.unlisten(this.player, "adaptation");
    };
  }

  onEvent(
    event: PlayerEvent | PlayerEvent[],
    callback: () => void,
  ): () => void {
    const videoElement = this.player?.getMediaElement() as HTMLVideoElement;
    if (!this.eventManager || !videoElement) {
      console.warn("Cannot attach event listener due to missing components");
      return () => {};
    }

    const events = Array.isArray(event) ? event : [event];

    events.forEach((evt) => {
      this.eventManager.listen(videoElement, evt, callback);
    });

    return () => {
      events.forEach((evt) => {
        this.eventManager.unlisten(videoElement, evt, callback);
      });
    };
  }

  setVideoTrack(id: string | "auto") {
    const tracks = this.player?.getVariantTracks();

    if (!tracks || tracks.length === 0 || !this.player) {
      return;
    }

    if (id === "auto") {
      this.player.configure({
        abr: {
          enabled: true,
        },
      });
    }

    for (const track of tracks) {
      const isVideoTrack =
        track.type === "variant" && typeof track.videoId === "number";

      if (isVideoTrack && track.id.toString() === id) {
        this.player.configure({
          abr: {
            enabled: false,
          },
        });
        this.player?.selectVariantTrack(track, /* clearBuffer */ true);
        break;
      }
    }
  }

  setAudioTrack(audioTrackId: PlayerAudioTrack["id"]) {
    if (!this.player) {
      return;
    }

    const availableLanguages = this.player.getAudioLanguages();
    const { label, language } = this._getInfoFromAudioTrackId(audioTrackId);
    if (!availableLanguages.includes(language)) {
      return;
    }

    this.player.configure({
      abr: {
        enabled: true,
      },
    });
    this.player.selectVariantsByLabel(label);
  }

  setSubtitleTrack(id: string | "disabled") {
    const tracks = this.player?.getTextTracks();

    if (!tracks || tracks.length === 0) {
      return;
    }

    const shouldDisable = id === "disabled";

    if (shouldDisable) {
      this.player?.setTextTrackVisibility(false);
      return;
    }

    for (const track of tracks) {
      if (track.id.toString() === id) {
        this.player?.selectTextTrack(track);
        this.player?.setTextTrackVisibility(true);
        return;
      }
    }
  }

  setError(message: string, messageKey?: string) {
    const event = new CustomEvent("custom-error", {
      detail: {
        message: message,
        messageKey: messageKey,
      },
    });

    this.player?.getMediaElement()?.dispatchEvent(event);
  }

  error() {
    if (this.lastError) {
      return {
        type: getErrorMessageByCategoryCode(this.lastError.category.toString()),
      };
    }
    return null;
  }

  reload() {
    const { videoElement, onReload } = this.options;

    if (this.currentMediaState && videoElement) {
      const { source, startPosition, autoplay } = this.currentMediaState;

      onReload();
      this.initialize();
      this.clientSideAdvertisement?.onPlayerReload();
      this.loadMedia(source, autoplay, startPosition);
    }
  }

  adCuePoints() {
    return this.clientSideAdvertisement?.cuePoints || [];
  }

  requestNativeFullScreen() {
    return this.player?.getMediaElement()?.requestFullscreen() as Promise<void>;
  }

  exitNativeFullScreen() {
    if (document.fullscreenElement) {
      return document.exitFullscreen();
    }

    return Promise.resolve();
  }

  isNativeFullScreen(): boolean {
    return document.fullscreenElement === this.player?.getMediaElement();
  }

  toggleNativeFullScreen(): Promise<void> {
    return this.isNativeFullScreen()
      ? this.exitNativeFullScreen()
      : this.requestNativeFullScreen();
  }

  currentAdTracker() {
    return this.clientSideAdvertisement?.tracker;
  }

  pauseAd(): void {
    this.clientSideAdvertisement?.pause();
  }

  resumeAd(): void {
    this.clientSideAdvertisement?.resume();
  }

  skipAd(): void {
    this.clientSideAdvertisement?.skip();
  }

  private setupCastSender(videoElement: HTMLVideoElement) {
    if (this.castSender) {
      return;
    }

    this.castSender = new CastSender({
      onConnected: () => {
        const sourceId = this.currentMediaState?.source.id;
        if (sourceId) {
          this.castSender?.load({
            source: `${sourceId}`,
            sessionToken: this.options.sessionToken,
            startTime: videoElement.currentTime || 0,
          });
        }
        videoElement.pause();
        videoElement.dispatchEvent(
          new CustomEvent("castconnected", { bubbles: true }),
        );
        videoElement.dispatchEvent(new Event("volumechange"));
      },
      onDisconnected: () => {
        const latestTimeFromRemote = this.castSender?.getCurrentTime();
        if (latestTimeFromRemote) {
          videoElement.currentTime = latestTimeFromRemote;
        }
        videoElement.dispatchEvent(
          new CustomEvent("castdisconnected", { bubbles: true }),
        );
        videoElement.dispatchEvent(new Event("volumechange"));
        setTimeout(() => {
          videoElement.play();
        }, 1000);
      },
      onStateChanged: (
        newState: ChromecastPlayerState,
        oldState: ChromecastPlayerState,
      ) => {
        if (!this.isCastConnected()) {
          return;
        }
        if (newState.playbackState != oldState.playbackState) {
          switch (newState.playbackState) {
            case PlaybackState.PLAYING:
              videoElement.dispatchEvent(new Event("playing"));
              break;
            case PlaybackState.PAUSED:
              videoElement.dispatchEvent(new Event("pause"));
              break;
            case PlaybackState.ENDED:
              videoElement.dispatchEvent(new Event("ended"));
              break;
          }
        }
        if (newState.currentTime != oldState.currentTime) {
          videoElement.dispatchEvent(new Event("timeupdate"));
        }
      },
      onVolumeLevelChange: () => {
        videoElement.dispatchEvent(new Event("volumechange"));
      },
      onMuteChanged: () => {
        videoElement.dispatchEvent(new Event("volumechange"));
      },
      onError: (error) => {
        if (this.player && videoElement) {
          videoElement.dispatchEvent(
            new CustomEvent("custom-error", {
              bubbles: true,
              detail: error,
            }),
          );
        }
      },
    });
  }

  isCastConnected(): boolean {
    return Boolean(this.castSender?.isConnected());
  }

  getCastDeviceName(): string {
    if (this.castSender?.isConnected()) {
      return this.castSender?.getDeviceName();
    }

    return "";
  }

  toggleAirPlay() {
    const videoElement = this.player?.getMediaElement();
    // @ts-ignore
    videoElement?.webkitShowPlaybackTargetPicker?.();
  }

  onAirPlayAvailabilityChange(callback: (e: AirPlayAvailability) => void) {
    const videoElement = this.player?.getMediaElement();
    const handleAvailabilityChange = (e: AirPlayAvailabilityChangeEvent) => {
      callback(e.availability);
    };
    // @ts-ignore
    videoElement.addEventListener(
      AirPlayEvent.AVAILABILITY_CHANGED,
      handleAvailabilityChange,
    );

    return () => {
      // @ts-ignore
      videoElement.removeEventListener(
        AirPlayEvent.AVAILABILITY_CHANGED,
        handleAvailabilityChange,
      );
    };
  }

  onAirPlayStatusChange(callback: (isCasting: boolean) => void) {
    const videoElement = this.player?.getMediaElement();
    const handleTargetChange = () => {
      // @ts-ignore
      const isCasting = videoElement?.webkitCurrentPlaybackTargetIsWireless;
      callback(isCasting);
    };
    // @ts-ignore
    videoElement.addEventListener(
      AirPlayEvent.TARGET_CHANGED,
      handleTargetChange,
    );
    return () => {
      // @ts-ignore
      videoElement.removeEventListener(
        AirPlayEvent.TARGET_CHANGED,
        handleTargetChange,
      );
    };
  }

  onEnterFullScreen(): void {
    if (!this.isOnFullScreen) {
      this.isOnFullScreen = true;
      this.clientSideAdvertisement?.onEnterFullScreen();
    }
  }

  onLeaveFullScreen(): void {
    if (this.isOnFullScreen) {
      this.isOnFullScreen = false;
      this.clientSideAdvertisement?.onLeaveFullScreen();
    }
  }

  private _createAudioTrackId(
    label: PlayerAudioTrack["label"],
    language: PlayerAudioTrack["language"],
  ): PlayerAudioTrack["id"] {
    return `${label}:::${language}`;
  }

  private _getInfoFromAudioTrackId(
    audioTrackId: PlayerAudioTrack["id"],
  ): Pick<PlayerAudioTrack, "label" | "language"> {
    const [label, language] = audioTrackId.split(":::");
    return { label, language };
  }

  private configureDRM = async (source: PlayerSource) => {
    if (!this.player) {
      return;
    }

    if (isFairPlaySource(source) && this.fairPlayLoader) {
      // Use legacy Apple Media Keys for EZDRM
      shaka.polyfill.PatchedMediaKeysApple.install();

      const { certificateUri, licenseUri } =
        source.keySystems["com.apple.fps.1_0"];

      this.fairPlayLoader.initialize(certificateUri, licenseUri);
      const certificate = await this.fairPlayLoader?.loadCertificate();
      const playerRef = this.player;

      this.player.configure({
        drm: {
          servers: {
            "com.apple.fps.1_0": licenseUri,
          },
          advanced: {
            "com.apple.fps.1_0": {
              serverCertificate: certificate,
            },
          },
          initDataTransform: (initData: ArrayBuffer) => {
            const skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData);
            const contentId = skdUri.substring(skdUri.indexOf("skd://") + 6);
            const cert = playerRef.drmInfo()?.serverCertificate as Uint8Array;

            return shaka.util.FairPlayUtils.initDataTransform(
              initData,
              contentId,
              cert,
            );
          },
        },
      });

      this.player
        ?.getNetworkingEngine()
        ?.registerRequestFilter(function (type, request) {
          if (type == shaka.net.NetworkingEngine.RequestType.LICENSE) {
            const originalPayload = new Uint8Array(request.body as ArrayBuffer);
            const base64Payload =
              shaka.util.Uint8ArrayUtils.toBase64(originalPayload);
            const params = "spc=" + encodeURIComponent(base64Payload);

            request.body = shaka.util.StringUtils.toUTF8(params);
          }
        });

      this.player
        ?.getNetworkingEngine()
        ?.registerResponseFilter((type, response) => {
          if (type == shaka.net.NetworkingEngine.RequestType.LICENSE) {
            const responseText = shaka.util.StringUtils.fromUTF8(
              response.data,
            ).trim();
            response.data =
              shaka.util.Uint8ArrayUtils.fromBase64(responseText).buffer;
          }
        });
    }

    if (isWidevineSource(source)) {
      this.player?.configure({
        drm: {
          servers: {
            "com.widevine.alpha": source.keySystems["com.widevine.alpha"],
          },
          advanced: isAndroid()
            ? {
                "com.widevine.alpha": {
                  videoRobustness: "HW_SECURE_ALL",
                  audioRobustness: "HW_SECURE_CRYPTO",
                },
              }
            : undefined,
        },
      });
    }

    if (isPlayReadySource(source)) {
      this.player?.configure({
        drm: {
          servers: {
            "com.microsoft.playready":
              source.keySystems["com.microsoft.playready"],
          },
        },
      });
    }
  };

  private clearLiveRecoveryTimer() {
    if (this.liveRecoveryTimeoutId !== null) {
      window.clearTimeout(this.liveRecoveryTimeoutId);
      this.liveRecoveryTimeoutId = null;
    }
  }
}
